import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { StorageService } from './storage.service';
import { first } from 'rxjs/operators';
import { CalcCssVarsService } from '@core/services/calc-css-vars.service';

export interface AccessibilitySettings {
    isHighContrast: boolean;
    fontSize: number;
    isEnabled: boolean;
}

const DEFAULT_SETTINGS: AccessibilitySettings = {
    isHighContrast: false,
    fontSize: 1,
    isEnabled: false,
};

const STORAGE_KEY = 'accessibility-settings';
const MIN_FONT_SIZE = 0.7;
const MAX_FONT_SIZE = 1.5;
const FONT_STEP = 0.1;

@Injectable({
    providedIn: 'root',
})
export class AccessibilityService {
    private readonly calcCssVarsService = inject(CalcCssVarsService);
    private readonly storageService = inject(StorageService);

    private settingsSubject = new BehaviorSubject<AccessibilitySettings>(DEFAULT_SETTINGS);
    public settings$ = this.settingsSubject.asObservable();

    private panelVisibleSubject = new BehaviorSubject<boolean>(false);
    public panelVisible$ = this.panelVisibleSubject.asObservable();

    constructor() {
        this.loadSettings();
    }

    public togglePanel(): void {
        this.panelVisibleSubject.next(!this.panelVisibleSubject.value);
    }

    public hidePanel(): void {
        this.panelVisibleSubject.next(false);
    }

    public toggleHighContrast(): void {
        const currentSettings = this.settingsSubject.value;
        const newSettings = {
            ...currentSettings,
            isHighContrast: !currentSettings.isHighContrast,
            isEnabled: true,
        };
        this.updateSettings(newSettings);
    }

    public increaseFontSize(): void {
        const currentSettings = this.settingsSubject.value;
        const newFontSize = Math.min(currentSettings.fontSize + FONT_STEP, MAX_FONT_SIZE);
        const newSettings = {
            ...currentSettings,
            fontSize: Math.round(newFontSize * 10) / 10,
            isEnabled: true,
        };
        this.updateSettings(newSettings);
    }

    public decreaseFontSize(): void {
        const currentSettings = this.settingsSubject.value;
        const newFontSize = Math.max(currentSettings.fontSize - FONT_STEP, MIN_FONT_SIZE);
        const newSettings = {
            ...currentSettings,
            fontSize: Math.round(newFontSize * 10) / 10,
            isEnabled: true,
        };
        this.updateSettings(newSettings);
    }

    public resetSettings(): void {
        this.updateSettings(DEFAULT_SETTINGS);
        this.storageService.remove(STORAGE_KEY).pipe(first()).subscribe();
    }

    public getCurrentSettings(): AccessibilitySettings {
        return this.settingsSubject.value;
    }

    private updateSettings(settings: AccessibilitySettings): void {
        this.settingsSubject.next(settings);
        this.applySettings(settings);
        this.saveSettings(settings);
    }

    private applySettings(settings: AccessibilitySettings): void {
        const root = document.documentElement;

        if (settings.isHighContrast) {
            root.classList.add('accessibility-high-contrast');
        } else {
            root.classList.remove('accessibility-high-contrast');
        }

        root.style.setProperty('--zoom', settings.fontSize.toString());

        if (settings.isEnabled && (settings.isHighContrast || settings.fontSize !== 1)) {
            root.classList.add('accessibility-enabled');
        } else {
            root.classList.remove('accessibility-enabled');
        }

        this.calcCssVarsService.reinitialize();
    }

    private saveSettings(settings: AccessibilitySettings): void {
        if (settings.isEnabled && (settings.isHighContrast || settings.fontSize !== 1)) {
            this.storageService.set(STORAGE_KEY, settings).pipe(first()).subscribe();
        } else {
            this.storageService.remove(STORAGE_KEY).pipe(first()).subscribe();
        }
    }

    private loadSettings(): void {
        this.storageService
            .get<AccessibilitySettings>(STORAGE_KEY)
            .pipe(first())
            .subscribe((savedSettings) => {
                if (savedSettings) {
                    this.settingsSubject.next(savedSettings);
                    this.applySettings(savedSettings);
                }
            });
    }
}
